import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";

import SectionWrapper from "../SectionWrapper";
import styles from "./TocaBillableoverview.module.css";
import MonthYearPicker from "../../shared-modules/DatePicker/MonthYearPicker";
import Skeleton from "../Skeleton";
import { getRampUpAndBillablePatients } from "../../../services/super-admin-dashboard/financialOverview";

/* ============================= */
/* ===== Radial Progress ======= */
/* ============================= */

const RadialProgress = ({ label, value = 0, color = "#1b254b" }) => {
  const safeValue = Math.min(Math.max(value ?? 0, 0), 100);
  const data = [{ name: label, value: safeValue }];

  return (
    <div className={styles.radialCard}>
      <RadialBarChart
        width={160}
        height={160}
        cx="50%"
        cy="50%"
        innerRadius="70%"
        outerRadius="100%"
        barSize={12}
        data={data}
        startAngle={90}
        endAngle={-270}
      >
        <PolarAngleAxis
          type="number"
          domain={[0, 100]}
          tick={false}
        />
        <RadialBar
          background
          clockWise
          dataKey="value"
          // cornerRadius={20}
          fill={color}
        />
      </RadialBarChart>

      <div className={styles.radialCenter}>
        {safeValue.toFixed(1)}%
      </div>

      <div className={styles.radialLabel}>{label}</div>
    </div>
  );
};

/* ============================= */
/* ===== Breakdown Column ====== */
/* ============================= */

const BreakdownColumn = ({
  title,
  data = {},
  isLoading,
  showExtraBillable = false,
  showBillablePatients = false,
}) => {
  if (isLoading) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div>{title}</div>
          <Skeleton width="60px" height="28px" radius="8px" />
        </div>
        <Skeleton width="100%" height="120px" />
      </div>
    );
  }
    const totalPatients =(data?.rpm_only ?? 0) + (data?.ccm_only ?? 0) +(data?.rpm_ccm ?? 0);
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderTitle}>{title}</div>
       <div className={styles.cardHeaderTotal}>
  <span>Total Patients:</span>
  <span className={styles.cardTotalValue}>
    {totalPatients}
  </span>
</div>
      </div>

      {/* KPIs (primary + extra) rendered in a single row */}
      <div className={styles.smallKpiRow}>
        <div className={styles.smallKpi}>
          <span>RPM only</span>
          <strong>{data.rpm_only ?? 0}</strong>
        </div>

        <div className={styles.smallKpi}>
          <span>CCM only</span>
          <strong>{data.ccm_only ?? 0}</strong>
        </div>

        <div className={styles.smallKpi}>
          <span>RPM + CCM</span>
          <strong>{data.rpm_ccm ?? 0}</strong>
        </div>

        {showExtraBillable && (
          <>
            <div className={styles.smallKpi}>
              <span>RPM Billable</span>
              <strong>{data.patients_with_rpm_billable ?? 0}</strong>
            </div>

            <div className={styles.smallKpi}>
              <span>CCM Billable</span>
              <strong>{data.patients_with_ccm_billable ?? 0}</strong>
            </div>
          </>
        )}

        {showBillablePatients && (
          <div className={styles.smallKpi}>
            <span>Billable Patients</span>
            <strong>{data.billable_patients ?? 0}</strong>
          </div>
        )}
      </div>

      {/* ===== Radial Charts Section ===== */}
      <div className={styles.radialGrid}>
        {data.pct_with_rpm_billable !== undefined && (
          <RadialProgress
            label="RPM Billable"
            value={data.pct_with_rpm_billable}
            color="#1b254b"
          />
        )}

        {data.pct_with_ccm_billable !== undefined && (
          <RadialProgress
            label="CCM Billable"
            value={data.pct_with_ccm_billable}
            color="#3b82f6"
          />
        )}

        {data.pct_billable_of_gte_90 !== undefined && (
          <RadialProgress
            label="Billable Patients"
            value={data.pct_billable_of_gte_90}
            color="#7c3aed"
          />
        )}
      </div>
    </div>
  );
};

/* ============================= */
/* ===== Main Component ======== */
/* ============================= */

const TocaBillableOverview = ({ scope = "all" }) => {
  const [selectedMonthYear, setSelectedMonthYear] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["ramp-up-and-billable", scope, selectedMonthYear],
    queryFn: () =>
      getRampUpAndBillablePatients(scope, selectedMonthYear || undefined),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <SectionWrapper
      title="TOCA Billing Overview"
      // headerRight={
      //   <MonthYearPicker
      //     value={selectedMonthYear}
      //     onChange={setSelectedMonthYear}
      //   />
      // }
    >
      <div className={styles.container}>
        <BreakdownColumn
          title="Patients Enrolled < 90 Days"
          data={data?.lt_90_days}
          isLoading={isLoading}
          showExtraBillable
        />

        <BreakdownColumn
          title="Patients Enrolled ≥ 90 Days (Billable)"
          data={data?.gte_90_days_billable}
          isLoading={isLoading}
          showBillablePatients
        />
      </div>
    </SectionWrapper>
  );
};

export default TocaBillableOverview;